// 2FA rework (2026-08-17) — /2fa/verify, the login code step.
//
// SignInForm navigates here when the login envelope has
// requires_2fa_step = TRUE (the user has TOTP enrolled). The backend
// session is a PARTIAL one (5-minute TTL) whose only reachable endpoints
// are the 2FA allowlist — so this page must not depend on /account/me.
//
// POST /api/v1/auth/2fa-verify accepts a 6-digit TOTP code OR one of the
// single-use backup codes. Success flips the session to full → /feed.
// A 401 here usually means the 5-minute partial window lapsed: send the
// user back to `/` to sign in again.
//
// CHROMELESS route (App.tsx) — owns its full-screen layout like the
// landing; a half-authenticated visitor gets no app chrome.

import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Input, Label } from "@greenhold/components";

import {
  useTwoFactorVerify,
  type TwoFactorError,
} from "../features/two-factor/useTwoFactor";

export function TwoFactorVerifyPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const verify = useTwoFactorVerify();

  const [code, setCode] = useState("");
  const [error, setError] = useState<TwoFactorError | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      await verify.mutateAsync({ code: code.trim() });
      await queryClient.invalidateQueries({ queryKey: ["account-me"] });
      navigate("/feed", { replace: true });
    } catch (err) {
      setError(err as TwoFactorError);
      setCode("");
    }
  };

  const errorMessage = mapErrorMessage(error);
  const sessionLapsed = error !== null && error.status === 401 && error.code !== "TWO_FACTOR_INVALID";

  return (
    <main className="min-h-screen bg-brand-forest-900 text-brand-paper-100 flex items-center justify-center px-6">
      <div className="w-full max-w-md rounded border border-brand-gold-500/40 bg-brand-forest-800 p-8 flex flex-col gap-4">
        <h1 className="font-heading text-2xl m-0 text-brand-gold-300">
          Two-factor authentication
        </h1>
        <p className="font-body text-sm text-brand-paper-100/80 m-0 leading-relaxed">
          Enter the 6-digit code from your authenticator app, or one of your
          backup codes.
        </p>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
          data-testid="two-factor-verify-form"
          noValidate
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="two-factor-code" className="text-brand-paper-100">
              Code
            </Label>
            <Input
              id="two-factor-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              disabled={verify.isPending}
              aria-describedby={errorMessage ? "two-factor-error" : undefined}
              className="text-brand-forest-900"
            />
            {errorMessage ? (
              <p
                id="two-factor-error"
                role="alert"
                className="text-sm text-state-error-accent"
              >
                {errorMessage}
              </p>
            ) : null}
          </div>
          <Button
            type="submit"
            variant="primary"
            disabled={verify.isPending || code.trim().length === 0}
            aria-busy={verify.isPending}
            className="w-full hover:brightness-110"
            data-testid="two-factor-verify-submit"
          >
            {verify.isPending ? "Verifying…" : "Verify"}
          </Button>
        </form>
        {sessionLapsed ? (
          <p className="font-body text-sm text-brand-paper-100/80 m-0">
            <Link to="/" className="underline hover:text-brand-gold-300">
              Return to sign-in
            </Link>
          </p>
        ) : null}
      </div>
    </main>
  );
}

function mapErrorMessage(error: TwoFactorError | null): string | null {
  if (!error) return null;
  switch (error.code) {
    case "TWO_FACTOR_INVALID":
      return "That code didn't match. Check your authenticator and try again.";
    case "RATE_LIMITED":
      return "Too many attempts. Try again in a few minutes.";
    default:
      if (error.status === 401) {
        return "Your sign-in window expired. Please sign in again.";
      }
      return error.message || "Verification failed. Try again.";
  }
}
